// OnlyOffice Editor Component with JWT Support
import React, { useEffect, useState, useRef, Component, ErrorInfo, forwardRef, useImperativeHandle } from 'react';
import { DocumentEditor } from '@onlyoffice/document-editor-react';
import { Box, Alert, CircularProgress } from '@mui/material';
import axios from 'axios';
import { getApiBaseUrl, getOnlyOfficeServerUrl } from '../utils/apiUrl';

// Error Boundary für OnlyOffice DOM-Cleanup-Fehler
class OnlyOfficeErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    // Ignoriere DOM-Cleanup-Fehler von OnlyOffice
    return { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Logge nur echte Fehler, nicht DOM-Cleanup-Probleme
    if (!error.message.includes('removeChild')) {
      console.error('OnlyOffice Error:', error, errorInfo);
    }
  }

  render() {
    return this.props.children;
  }
}

export interface OnlyOfficeEditorHandle {
  insertContent: (htmlContent: string) => void;
  getHTML: () => string | undefined;
  getSelectedText: () => string;
  replaceSelection: (text: string) => void;
  getDocumentText: () => string;
  insertText?: (text: string) => void;
}

interface OnlyOfficeEditorProps {
  documentId: number;
  height?: string;
}

const OnlyOfficeEditor = forwardRef<OnlyOfficeEditorHandle, OnlyOfficeEditorProps>(({
  documentId,
  height = '800px',
}, ref) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<any>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const docEditorRef = useRef<any>(null);
  const connectorRef = useRef<any>(null);

  // OnlyOffice Server URL - dynamisch basierend auf Umgebung
  const onlyOfficeServerUrl = getOnlyOfficeServerUrl();
  const apiBaseUrl = getApiBaseUrl();

  // HTML zu reinem Text konvertieren
  const htmlToPlainText = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    insertContent: async (htmlContent: string) => {
      console.log('OnlyOffice: insertContent called with:', htmlContent);

      // Text aus HTML extrahieren
      const plainText = htmlToPlainText(htmlContent);
      console.log('OnlyOffice: Plain text to insert:', plainText);

      // Finde das OnlyOffice iframe
      const iframe = document.querySelector('[id^="onlyoffice-editor"] iframe') as HTMLIFrameElement;

      // Methode 1: Versuche über Connector API
      if (connectorRef.current) {
        try {
          console.log('OnlyOffice: Trying connector.callCommand...');
          connectorRef.current.callCommand(
            function() {
              // @ts-ignore
              var oDocument = Api.GetDocument();
              // @ts-ignore
              var oParagraph = Api.CreateParagraph();
              // @ts-ignore
              oParagraph.AddText(arguments[0]);
              oDocument.InsertContent([oParagraph]);
            },
            function() {
              console.log('OnlyOffice: Text inserted via callCommand');
            },
            true, false, [plainText]
          );
          showNotification('Textbaustein eingefügt!', 'success');
          return;
        } catch (err) {
          console.warn('OnlyOffice: callCommand failed:', err);
        }
      }

      // Methode 2: Versuche über interne OnlyOffice API (asc_docs_api)
      try {
        // Suche nach der internen API im window-Objekt
        const windowKeys = Object.keys(window);
        console.log('OnlyOffice: Searching for internal API in window...');

        for (const key of windowKeys) {
          const obj = (window as any)[key];
          if (obj && typeof obj === 'object') {
            // Suche nach asc_docs_api oder ähnlichen Objekten
            if (typeof obj.asc_addText === 'function' ||
                typeof obj.AddText === 'function' ||
                typeof obj.insertText === 'function') {
              console.log(`OnlyOffice: Found API on window.${key}:`, Object.keys(obj).slice(0, 20));
            }
          }
        }

        // Versuche DocsAPI Methoden
        const docsApi = (window as any).DocsAPI;
        if (docsApi) {
          console.log('OnlyOffice: DocsAPI keys:', Object.keys(docsApi));
          if (docsApi.DocEditor) {
            console.log('OnlyOffice: DocEditor keys:', Object.keys(docsApi.DocEditor));
          }
        }
      } catch (err) {
        console.warn('OnlyOffice: Internal API search failed:', err);
      }

      // Methode 2b: Versuche postMessage an OnlyOffice iframe
      if (iframe?.contentWindow) {
        try {
          console.log('OnlyOffice: Trying postMessage to iframe...');

          // Verschiedene Message-Formate probieren
          const messages = [
            { type: 'insertText', text: plainText },
            { type: 'onExternalPluginMessage', data: { type: 'insertText', text: plainText } },
            { command: 'insertText', data: plainText },
            { event: 'asc_onInputText', data: plainText }
          ];

          for (const msg of messages) {
            iframe.contentWindow.postMessage(JSON.stringify(msg), '*');
          }
          console.log('OnlyOffice: postMessage sent');

        } catch (err) {
          console.warn('OnlyOffice: postMessage failed:', err);
        }
      }

      // Methode 3: Versuche über execCommand (deprecated aber manchmal funktioniert es)
      if (iframe?.contentDocument) {
        try {
          console.log('OnlyOffice: Trying execCommand in iframe...');
          iframe.contentWindow?.focus();

          // Erstelle ein temporäres Element mit dem Text
          const tempInput = document.createElement('textarea');
          tempInput.value = plainText;
          document.body.appendChild(tempInput);
          tempInput.select();
          document.execCommand('copy');
          document.body.removeChild(tempInput);

          // Fokussiere iframe und führe paste aus
          iframe.focus();
          const pasteSuccess = iframe.contentDocument.execCommand('paste');
          console.log('OnlyOffice: execCommand paste result:', pasteSuccess);

          if (pasteSuccess) {
            showNotification('Textbaustein eingefügt!', 'success');
            return;
          }
        } catch (err) {
          console.warn('OnlyOffice: execCommand failed:', err);
        }
      }

      // Methode 4: Finale Fallback - Zwischenablage mit automatischem Fokus
      try {
        await navigator.clipboard.writeText(plainText);
        console.log('OnlyOffice: Text copied to clipboard');

        if (iframe) {
          // Fokussiere das iframe
          iframe.focus();

          // Simuliere Ctrl+V mit ClipboardEvent
          try {
            const clipboardData = new DataTransfer();
            clipboardData.setData('text/plain', plainText);

            const pasteEvent = new ClipboardEvent('paste', {
              bubbles: true,
              cancelable: true,
              clipboardData: clipboardData
            });

            iframe.contentDocument?.body?.dispatchEvent(pasteEvent);
            console.log('OnlyOffice: ClipboardEvent dispatched');
          } catch (clipErr) {
            console.warn('OnlyOffice: ClipboardEvent failed:', clipErr);
          }
        }

        showNotification('Textbaustein kopiert! Drücken Sie Strg+V zum Einfügen.', 'info');
      } catch (clipboardErr) {
        console.error('OnlyOffice: Clipboard fallback failed:', clipboardErr);
        alert('Fehler beim Kopieren. Bitte kopieren Sie den Inhalt manuell: ' + plainText);
      }
    },
    getHTML: () => {
      console.log('OnlyOffice: getHTML called');
      // OnlyOffice speichert automatisch, wir können hier undefined zurückgeben
      return undefined;
    },
    getSelectedText: (): string => {
      console.log('OnlyOffice: getSelectedText called');
      // OnlyOffice bietet keine einfache Möglichkeit, ausgewählten Text zu lesen
      // Der Benutzer muss den Text manuell kopieren
      return '';
    },
    replaceSelection: (text: string) => {
      console.log('OnlyOffice: replaceSelection called with:', text);
      // Versuche Text über Connector einzufügen
      if (connectorRef.current) {
        try {
          connectorRef.current.callCommand(
            function() {
              // @ts-ignore
              var oDocument = Api.GetDocument();
              // @ts-ignore
              var oParagraph = Api.CreateParagraph();
              // @ts-ignore
              oParagraph.AddText(arguments[0]);
              oDocument.InsertContent([oParagraph]);
            },
            function() {
              console.log('OnlyOffice: Text replaced via callCommand');
            },
            true, false, [text]
          );
        } catch (err) {
          console.warn('OnlyOffice: replaceSelection failed:', err);
        }
      }
    },
    getDocumentText: (): string => {
      console.log('OnlyOffice: getDocumentText called');
      // OnlyOffice bietet keine einfache Möglichkeit, den gesamten Text zu lesen
      return '';
    },
    insertText: (text: string) => {
      console.log('OnlyOffice: insertText called');
      if (connectorRef.current) {
        try {
          connectorRef.current.callCommand(
            function() {
              // @ts-ignore
              var oDocument = Api.GetDocument();
              // @ts-ignore
              var oParagraph = Api.CreateParagraph();
              // @ts-ignore
              oParagraph.AddText(arguments[0]);
              oDocument.InsertContent([oParagraph]);
            },
            function() {
              console.log('OnlyOffice: Text inserted');
            },
            true, false, [text]
          );
        } catch (err) {
          console.warn('OnlyOffice: insertText failed:', err);
        }
      }
    },
  }));

  // Hilfsfunktion für Benachrichtigungen
  const showNotification = (message: string, type: 'success' | 'info' | 'error') => {
    const colors = {
      success: '#4caf50',
      info: '#2196f3',
      error: '#f44336'
    };
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="position: fixed; top: 20px; right: 20px; background: ${colors[type]}; color: white; padding: 16px 24px; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3); z-index: 10000; font-family: sans-serif;">
        ${message}
      </div>
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  };

  const onDocumentReady = (event: any) => {
    console.log('OnlyOffice Document is ready', event);

    // Speichere die DocEditor-Instanz
    if (event && event.target) {
      docEditorRef.current = event.target;
      console.log('OnlyOffice: DocEditor constructor saved');
    }

    // Versuche Connector zu erstellen für erweiterte API-Funktionen
    const editorId = `onlyoffice-editor-${documentId}-${config?.document?.key}`;
    console.log('OnlyOffice: Looking for editor with ID:', editorId);

    try {
      // Methode 1: Suche nach allen window-Eigenschaften mit createConnector
      if (!connectorRef.current) {
        for (const key of Object.keys(window)) {
          const obj = (window as any)[key];
          if (obj && typeof obj === 'object' && typeof obj.createConnector === 'function') {
            console.log(`OnlyOffice: Found createConnector on window.${key}`);
            connectorRef.current = obj.createConnector();
            console.log('OnlyOffice: Connector created via window.' + key);
            break;
          }
        }
      }

      // Methode 2: Suche im DocsAPI.DocEditor.instances
      if (!connectorRef.current) {
        const docsApi = (window as any).DocsAPI;
        if (docsApi?.DocEditor?.instances) {
          console.log('OnlyOffice: DocsAPI.DocEditor.instances found');
          const instances = docsApi.DocEditor.instances;
          for (const instKey of Object.keys(instances)) {
            const inst = instances[instKey];
            if (inst && typeof inst.createConnector === 'function') {
              connectorRef.current = inst.createConnector();
              console.log('OnlyOffice: Connector created via DocsAPI.DocEditor.instances');
              break;
            }
          }
        }
      }

      // Methode 3: Finde das iframe und kommuniziere über postMessage
      if (!connectorRef.current) {
        const container = document.getElementById(editorId);
        const iframe = container?.querySelector('iframe') || document.querySelector(`[id^="onlyoffice-editor"] iframe`);
        if (iframe) {
          console.log('OnlyOffice: Found iframe element');
          // Speichere iframe für später
          (window as any)._onlyOfficeIframe = iframe;
        }
      }

    } catch (err) {
      console.warn('OnlyOffice: Could not create connector:', err);
    }

    console.log('OnlyOffice: Connector available:', !!connectorRef.current);

    setLoading(false);
  };

  const onLoadComponentError = (errorCode: any, errorDescription: any) => {
    console.error('OnlyOffice Load Error:', errorCode, errorDescription);
    setError(`Fehler beim Laden des Editors: ${errorDescription || errorCode}`);
    setLoading(false);
  };

  const onError = (event: any) => {
    console.error('OnlyOffice Error:', event);
    console.error('Event Detail:', {
      data: event?.data,
      target: event?.target,
      type: event?.type
    });
    setError('Ein Fehler ist beim Laden des Dokuments aufgetreten. Prüfen Sie, ob der OnlyOffice Server läuft und das Backend erreichbar ist.');
    setLoading(false);
  };

  // Load config from backend
  useEffect(() => {
    const loadConfig = async () => {
      try {
        if (!onlyOfficeServerUrl || onlyOfficeServerUrl === 'http://localhost:80') {
          setError('OnlyOffice Server URL ist nicht konfiguriert. Bitte setzen Sie REACT_APP_ONLYOFFICE_SERVER_URL in der .env Datei.');
          setLoading(false);
          return;
        }

        console.log('Loading OnlyOffice config from backend...');

        const authToken = localStorage.getItem('skeena_token');
        const response = await axios.get(`${apiBaseUrl}/api/dokumente/${documentId}/onlyoffice-config`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        const { config: receivedConfig, token: jwtToken } = response.data;

        // Add JWT token to config (ohne events - die werden als props übergeben)
        const configWithToken = {
          ...receivedConfig,
          token: jwtToken
        };

        setConfig(configWithToken);

        console.log('=== ONLYOFFICE CONFIG (WITH JWT) ===');
        console.log('OnlyOffice Server:', onlyOfficeServerUrl);
        console.log('Config:', configWithToken);
        console.log('JWT Token:', jwtToken);
        console.log('====================================');

        setLoading(false);
      } catch (err: any) {
        console.error('Error loading OnlyOffice config:', err);
        setError(`Fehler beim Laden der OnlyOffice-Konfiguration: ${err.message}`);
        setLoading(false);
      }
    };

    loadConfig();
  }, [documentId, onlyOfficeServerUrl, apiBaseUrl]);

  // Cleanup on unmount - lasse OnlyOffice das DOM selbst aufräumen
  useEffect(() => {
    return () => {
      // Kein manuelles DOM-Cleanup - OnlyOffice räumt sich selbst auf
      // Das vermeidet Konflikte mit React's DOM-Management
      docEditorRef.current = null;
    };
  }, []);

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading || !config) {
    return (
      <Box sx={{ position: 'relative', width: '100%', height: height }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <OnlyOfficeErrorBoundary>
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: height,
          minHeight: height,
          '& iframe': {
            border: 'none',
            width: '100%',
            height: '100%',
          }
        }}
      >
        <DocumentEditor
          id={`onlyoffice-editor-${documentId}-${config.document.key}`}
          documentServerUrl={onlyOfficeServerUrl}
          config={config}
          events_onDocumentReady={onDocumentReady}
          events_onError={onError}
          onLoadComponentError={(errorCode: any, errorDescription: any) => {
            console.error('OnlyOffice Component Load Error:', errorCode, errorDescription);
            onLoadComponentError(errorCode, errorDescription);
          }}
        />
      </Box>
    </OnlyOfficeErrorBoundary>
  );
});

OnlyOfficeEditor.displayName = 'OnlyOfficeEditor';

export default OnlyOfficeEditor;
